/**
 * Modal
 *
 * Lightweight modal component used by the Simple mode focused-action flows.
 * Built as a portal to document.body with:
 * - Backdrop click → close
 * - Escape key → close
 * - Body scroll locked while open
 * - Basic focus management (focus the dialog on open)
 *
 * Not a full dialog library — intentionally minimal. If we ever need tabs,
 * composition, or forms-inside-modal gymnastics, swap for @radix-ui/react-dialog.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: ModalSize;
  /**
   * When true, clicking the backdrop does NOT close the modal.
   * Useful for modals that run long AI operations and we don't want
   * the user to lose progress by accident.
   */
  disableBackdropClose?: boolean;
  /**
   * Extra classes for the dialog surface (inner panel).
   */
  className?: string;
  /**
   * Extra classes for the scrollable body wrapper (default: px-6 py-4).
   * Pass "p-0" to remove padding and manage it yourself inside children.
   */
  bodyClassName?: string;
  /**
   * Optional footer, rendered at the bottom of the dialog.
   */
  footer?: ReactNode;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  disableBackdropClose = false,
  className,
  bodyClassName,
  footer,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus dialog on open
  useEffect(() => {
    if (open) {
      // Wait for portal mount
      const t = window.setTimeout(() => dialogRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disableBackdropClose) return;
      if (e.target === e.currentTarget) onClose();
    },
    [disableBackdropClose, onClose],
  );

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4 bg-[var(--backdrop-bg)] backdrop-blur-sm animate-in fade-in duration-150"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-description' : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-xl border border-[var(--modal-border)] bg-[var(--modal-bg)] text-[var(--modal-fg)] shadow-2xl outline-none',
          'flex flex-col max-h-[90dvh]',
          SIZE_CLASSES[size],
          'animate-in zoom-in-95 duration-150',
          className,
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || description) && (
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-[var(--modal-border)]">
            <div className="min-w-0 flex-1">
              {title && (
                <h2 id="modal-title" className="text-lg font-semibold leading-tight">
                  {title}
                </h2>
              )}
              {description && (
                <p id="modal-description" className="mt-1 text-sm text-[var(--modal-muted)]">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="shrink-0 rounded-md p-1 text-[var(--modal-muted)] hover:text-[var(--modal-fg)] hover:bg-black/5 transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>
        )}
        {!title && !description && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute top-3 right-3 z-10 rounded-md p-1 text-[var(--modal-muted)] hover:text-[var(--modal-fg)] hover:bg-black/5 transition-colors"
          >
            <X className="size-5" />
          </button>
        )}
        <div className={cn('flex-1 overflow-y-auto px-6 py-4', bodyClassName)}>{children}</div>
        {footer && (
          <div className="shrink-0 px-6 py-4 border-t border-[var(--modal-border)] bg-black/5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
